import React, { useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyList from "go-core/components/EmptyList";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import AppFormatResourceStatusLabel from "../../../../../../../../../../../components/FormatStatus";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, InvoicePaymentApi } from "../../../../../../../../../../../services/Api/Organization/types";
import InvoicePreviewPaymentsModal from "./InvoicePreviewPaymentsModal";

interface Props {
	invoice: InvoiceApi;
	fetchInvoice: () => Promise<void>;
}

const InvoicePreviewPaymentsCard = ({ invoice, fetchInvoice }: Props) => {
	const { t } = useTranslation();
	const [showInvoicePaymentsModal, setShowInvoicePaymentsModal] = useState(false);
	const payments = invoice.payments;
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();

	const handlePay = async (payment: InvoicePaymentApi) => {
		try {
			await apiOrganization.updateInvoicePayment(invoice.id, payment.id, { ...payment, paid: true });
			await fetchInvoice();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRemovePayment = async (payment: InvoicePaymentApi) => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			await apiOrganization.removeInvoicePayment(invoice.id, payment.id);
			await fetchInvoice();
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<>
			<Card className="card-box">
				<Card.Body>
					<div className="action-header">
						<h5 className="card-title">{t("modules.invoice.field.payments.title")}</h5>
						<Button variant="add" onClick={() => setShowInvoicePaymentsModal(true)}>
							+ {t("lib:common.action.add")}
						</Button>
					</div>
					{payments.length > 0 ? (
						<div className="table-responsive">
							<table className="table table-preview">
								<thead>
									<tr>
										<th>{t("modules.invoice.field.payment_type.title")}</th>
										<th>{t("modules.invoice.field.payment_date.title")}</th>
										<th>{t("modules.invoice.field.money_amount.title")}</th>
										<th className="action" style={{ width: "10%" }} />
									</tr>
								</thead>
								<tbody>
									{payments.map((payment) => {
										return (
											<tr key={`payment-${payment.id}`}>
												<td>
													<span
														className={`d-inline-block ${payment.paid ? "me-1" : "me-0"}`}
													>
														{t(`enums.invoice.payment_type.${payment.payment_type}`)}
													</span>
													{payment.paid && <AppFormatResourceStatusLabel status="PAID" />}
												</td>
												<td>{FormatDate(payment.payment_date)}</td>
												<td>{FormatMoney(payment.price_paid)}</td>
												<td className="actions">
													<div className="d-flex align-items-center gap-2 justify-content-end">
														{!payment.paid && (
															<Button onClick={() => handlePay(payment)}>
																{t("modules.invoice.action.pay.title")}
															</Button>
														)}
														<RemoveSVG
															className="icon"
															onClick={() => handleRemovePayment(payment)}
														/>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					) : (
						<EmptyList description={t("modules.invoice.field.empty_payments.title")} />
					)}
				</Card.Body>
			</Card>
			{showInvoicePaymentsModal && (
				<InvoicePreviewPaymentsModal
					invoice={invoice}
					show={showInvoicePaymentsModal}
					onHide={() => setShowInvoicePaymentsModal(false)}
					fetchInvoice={fetchInvoice}
				/>
			)}
		</>
	);
};

export default InvoicePreviewPaymentsCard;

// t('enums.common.status.PAID')
